import { useState, useRef } from "react";
import { Drawer, Box, Stack, Typography, Chip, Button, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import GitHubIcon from "@mui/icons-material/GitHub";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import { HEADING_FONT } from "../theme/fonts";
import { useToast } from "./ToastProvider";
import {
  useGithubStatus,
  useCreateGithubRepo,
  useUploadZipToRepo,
  useGithubUploadProgress,
} from "../hooks/queries";

// --- Spec palette (blue · light) ---
const BLUE = "#3B82F6";
const BLUE_DARK = "#1D4ED8";
const BLUE_SOFT = "#EFF6FF";
const INK = "#0F172A";
const MUTED = "#64748B";
const FAINT = "#94A3B8";
const HAIRLINE = "#EEF1F6";
const TILE_BG = "#F8FAFC";

function getExpiryStatus(domain) {
  if (!domain) return "Active";
  if (domain.status) return domain.status;
  if (!domain.expiry) return "Active";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(domain.expiry) >= today ? "Active" : "Expired";
}

// Same rule as the domain list: a site the last scan found not working is
// "Down", unless the domain has expired.
function getStatus(domain) {
  const status = getExpiryStatus(domain);
  if (domain && domain.websiteIsUp === false && status !== "Expired") return "Down";
  return status;
}

function daysUntil(date) {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((new Date(date) - today) / (1000 * 60 * 60 * 24));
}

// Status → spec colour band.
const STATUS_COLORS = {
  Active: { hex: "#16A34A", bg: "rgba(22, 163, 74, 0.12)" },
  "Near Expiry": { hex: "#D97706", bg: "rgba(217, 119, 6, 0.12)" },
  Expiring: { hex: "#D97706", bg: "rgba(217, 119, 6, 0.12)" },
  Expired: { hex: "#DC2626", bg: "rgba(220, 38, 38, 0.12)" },
  Down: { hex: "#DC2626", bg: "rgba(220, 38, 38, 0.12)" },
  New: { hex: BLUE, bg: BLUE_SOFT },
};
function statusColor(status) {
  return STATUS_COLORS[status] || STATUS_COLORS.Active;
}

// One label/value cell in the info grid.
function Field({ label, value, mono, copyable }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          color: FAINT,
          textTransform: "uppercase",
          letterSpacing: "0.6px",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 500,
            color: value && value !== "—" ? INK : FAINT,
            fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : "inherit",
            wordBreak: "break-word",
            minWidth: 0,
          }}
        >
          {value || "—"}
        </Typography>
        {copyable && value && value !== "—" && (
          <Tooltip title="Copy">
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(value)}
              sx={{ p: 0.25, color: MUTED, "&:hover": { color: BLUE } }}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

function SectionLabel({ icon, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
      <Box sx={{ color: BLUE, display: "flex" }}>{icon}</Box>
      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontSize: 13,
          fontWeight: 700,
          color: INK,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {children}
      </Typography>
    </Stack>
  );
}

// Right-side detail drawer for a single domain (per the RABS Workspace spec):
// gradient header, status + category badges, registrar/expiry/IP/server grid,
// a Client Details block, and a Sub-domains list. Footer: Edit / Close.
function DomainDetailDrawer({ domain, open, onClose, onEdit, canEdit = true }) {
  const toast = useToast();
  const githubStatus = useGithubStatus().data;
  const createRepo = useCreateGithubRepo();
  const uploadZip = useUploadZipToRepo();
  const uploadProgress = useGithubUploadProgress(domain?.id, uploadZip.isPending).data;
  // `domain` is the row the list handed over, so it won't carry a repo created
  // from inside this drawer. Keep that result here and clear it when another
  // domain is opened.
  const [repoInfo, setRepoInfo] = useState(null);
  const shownId = useRef(domain?.id);
  if (shownId.current !== domain?.id) {
    shownId.current = domain?.id;
    if (repoInfo) setRepoInfo(null);
  }
  const githubRepoUrl = repoInfo?.githubRepoUrl || domain?.githubRepoUrl || "";
  const githubRepoStatus = repoInfo?.githubRepoStatus || domain?.githubRepoStatus || "";

  async function handleCreateRepo() {
    try {
      setRepoInfo(await createRepo.mutateAsync(domain.id));
      toast.success("GitHub repo created");
    } catch (err) {
      toast.error(err.message || "Could not create the GitHub repo");
    }
  }

  // ZIP → repo. The file goes straight to the API and is never stored; the
  // repo's contents are replaced by the archive in one commit.
  async function handleZipPicked(event) {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-picking the same file after an error
    if (!file) return;
    const cap = githubStatus?.hardLimitMb || githubStatus?.zipLimitMb;
    if (cap && file.size > cap * 1048576) {
      toast.error(
        `"${file.name}" is ${(file.size / 1048576).toFixed(0)} MB. The limit is ${cap} MB — split it, or leave large media out of the archive.`
      );
      return;
    }
    try {
      const res = await uploadZip.mutateAsync({ id: domain.id, file });
      // The upload creates the repo when there wasn't one.
      if (res.domain) setRepoInfo(res.domain);
      toast.success(`${res.fileCount} file${res.fileCount === 1 ? "" : "s"} uploaded to the repo`);
    } catch (err) {
      toast.error(err.message || "ZIP upload failed");
    }
  }

  const status = getStatus(domain);
  const days = domain ? daysUntil(domain.expiry) : null;
  const subdomains = domain?.subdomains || [];
  const nameServers = domain?.nameServers || [];
  const externalUrl = domain?.name
    ? domain.name.startsWith("http")
      ? domain.name
      : `https://${domain.name}`
    : "#";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: "100%", sm: 560 },
            maxWidth: "100%",
            background: "#fff",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {domain && (
        <>
          {/* Gradient header (gradient is allowed on drawer/modal headers per spec) */}
          <Box
            sx={{
              position: "relative",
              background: `linear-gradient(150deg, ${BLUE_DARK} 0%, ${BLUE} 75%, #60A5FA 100%)`,
              color: "#fff",
              p: 3,
              flexShrink: 0,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    background: "rgba(255,255,255,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <LanguageIcon sx={{ fontSize: 26 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", opacity: 0.85 }}>
                    Domain
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontSize: 21,
                      fontWeight: 700,
                      lineHeight: 1.15,
                      wordBreak: "break-word",
                    }}
                  >
                    {domain.name || "(unnamed)"}
                  </Typography>
                </Box>
              </Stack>
              {/* × close — ml:auto pushes it to the absolute far-right */}
              <IconButton onClick={onClose} sx={{ color: "#fff", mt: -0.5, ml: "auto", flexShrink: 0 }}>
                <CloseIcon />
              </IconButton>
            </Stack>

            {/* Status + category + visit badges */}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={
                  days === null
                    ? status
                    : days < 0
                    ? `Expired ${Math.abs(days)}d ago`
                    : `${status} · ${days}d expiry`
                }
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  color: "#fff",
                  background: "rgba(255,255,255,0.22)",
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
              />
              {domain.category && (
                <Chip
                  size="small"
                  label={domain.category}
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#fff",
                    background: "rgba(255,255,255,0.16)",
                    border: "1px solid rgba(255,255,255,0.3)",
                  }}
                />
              )}
              <Chip
                size="small"
                component="a"
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                clickable
                icon={<OpenInNewIcon sx={{ fontSize: 14, color: `${BLUE_DARK} !important` }} />}
                label="Visit"
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  color: BLUE_DARK,
                  background: "#fff",
                  textDecoration: "none",
                  "&:hover": { background: "#f3f4f6" },
                }}
              />
              {/* GitHub: open the repo, create one, or push a ZIP into it —
                  without leaving the list. */}
              {githubRepoUrl ? (
                <Chip
                  size="small"
                  component="a"
                  href={githubRepoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  icon={<GitHubIcon sx={{ fontSize: 14, color: "#fff !important" }} />}
                  label="GitHub Repo"
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#fff",
                    background: "#24292f",
                    textDecoration: "none",
                    "&:hover": { background: "#32383f" },
                  }}
                />
              ) : (
                githubStatus?.enabled &&
                canEdit && (
                  <Chip
                    size="small"
                    clickable
                    onClick={handleCreateRepo}
                    icon={<GitHubIcon sx={{ fontSize: 14, color: "#fff !important" }} />}
                    label={
                      createRepo.isPending
                        ? "Creating…"
                        : githubRepoStatus === "failed"
                          ? "Retry GitHub Repo"
                          : "Create GitHub Repo"
                    }
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: "#fff",
                      background:
                        githubRepoStatus === "failed" ? "#b45309" : "rgba(255,255,255,0.22)",
                      border: "1px solid rgba(255,255,255,0.3)",
                    }}
                  />
                )
              )}
              {githubStatus?.enabled && canEdit && (
                // The label wraps the hidden input so the chip opens the file
                // picker (MUI's Chip renders no children of its own).
                <Box component="label" sx={{ display: "inline-flex", cursor: "pointer" }}>
                  <input
                    type="file"
                    accept=".zip,application/zip"
                    hidden
                    disabled={uploadZip.isPending}
                    onChange={handleZipPicked}
                  />
                  <Chip
                    size="small"
                    clickable
                    icon={<FolderZipOutlinedIcon sx={{ fontSize: 14, color: "#fff !important" }} />}
                    label={
                      !uploadZip.isPending
                        ? "Upload ZIP"
                        : uploadProgress?.phase === "committing" || uploadProgress?.phase === "pushing"
                          ? "Pushing…"
                          : uploadProgress?.files
                            ? `Uploading… ${uploadProgress.files} files`
                            : "Uploading…"
                    }
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      color: "#fff",
                      background: "rgba(255,255,255,0.22)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      pointerEvents: "none", // let the click reach the label
                    }}
                  />
                </Box>
              )}
            </Stack>
          </Box>

          {/* Scrollable body */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
            {/* Registrar / expiry / IP / server grid */}
            <SectionLabel icon={<LanguageIcon sx={{ fontSize: 18 }} />}>Domain & Server</SectionLabel>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                p: 2,
                borderRadius: "12px",
                border: `1px solid ${HAIRLINE}`,
                background: TILE_BG,
              }}
            >
              <Field label="Registrar" value={domain.registrar} />
              <Field label="Expiry Date" value={domain.expiry} />
              <Field
                label={
                  domain.ipAddresses?.length > 1
                    ? `IP Addresses (${domain.ipAddresses.length})`
                    : "IP Address"
                }
                value={
                  domain.ipAddresses?.length
                    ? domain.ipAddresses.join(", ")
                    : domain.ip
                }
                mono
                copyable
              />
              <Field label="Server" value={domain.serverName} copyable />
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Field label="Registered On" value={domain.registered} />
              </Box>
              {domain.sslValidity != null && (
                <Field label="SSL Validity" value={`${domain.sslValidity} days`} />
              )}
            </Box>

            {/* Name servers */}
            {nameServers.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Field
                  label="Name Servers"
                  value={nameServers.join(", ")}
                  mono
                />
              </Box>
            )}

            {/* Client details */}
            <Box sx={{ mt: 3 }}>
              <SectionLabel icon={<PersonOutlineOutlinedIcon sx={{ fontSize: 18 }} />}>Client Details</SectionLabel>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                  p: 2,
                  borderRadius: "12px",
                  border: `1px solid ${HAIRLINE}`,
                  background: TILE_BG,
                }}
              >
                <Field label="Client Code" value={domain.clientCode} copyable />
                <Field label="Phone" value={domain.clientPhone} copyable />
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Field label="Email" value={domain.clientEmail} copyable />
                </Box>
              </Box>
            </Box>

            {/* Sub-domains */}
            <Box sx={{ mt: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Box sx={{ color: BLUE, display: "flex" }}>
                  <LanOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography
                  sx={{
                    fontFamily: HEADING_FONT,
                    fontSize: 13,
                    fontWeight: 700,
                    color: INK,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Sub-domains
                </Typography>
                <Chip
                  size="small"
                  label={subdomains.length}
                  sx={{ height: 20, fontSize: 11, fontWeight: 700, background: BLUE_SOFT, color: BLUE_DARK }}
                />
              </Stack>

              {subdomains.length === 0 ? (
                <Box
                  sx={{
                    py: 3,
                    textAlign: "center",
                    color: FAINT,
                    borderRadius: "12px",
                    border: `1px dashed ${HAIRLINE}`,
                  }}
                >
                  <LanOutlinedIcon sx={{ fontSize: 28, mb: 0.5 }} />
                  <Typography sx={{ fontSize: 12.5 }}>No sub-domains</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {subdomains.map((sub) => {
                    const sBand = statusColor(sub.status === "Down" ? "Expired" : sub.status || "Active");
                    const isDown = sub.status === "Down";
                    const dotColor = isDown ? "#DC2626" : sBand.hex;
                    const dotBg   = isDown ? "rgba(220,38,38,0.12)" : sBand.bg;
                    return (
                      <Stack
                        key={sub.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          p: 1.5,
                          borderRadius: "12px",
                          border: `1px solid ${HAIRLINE}`,
                          background: TILE_BG,
                        }}
                      >
                        {/* Left: name + IP/SSL — flex:1 pushes badge to far right */}
                        <Box sx={{ minWidth: 0, flex: 1, pr: 1.5 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK, wordBreak: "break-all" }}>
                            {sub.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              color: MUTED,
                              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            }}
                          >
                            {sub.ip || "No IP"}
                            {sub.sslValidity != null ? ` · SSL ${sub.sslValidity}d` : ""}
                          </Typography>
                        </Box>
                        {/* Badge — rightmost, dot-pill matching DomainList style */}
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.6,
                            px: 1.25,
                            py: 0.4,
                            borderRadius: "999px",
                            fontWeight: 700,
                            fontSize: 11,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            color: dotColor,
                            background: dotBg,
                          }}
                        >
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: dotColor, flexShrink: 0 }} />
                          {sub.status || "—"}
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Box>

          {/* Footer: Edit / Close */}
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ p: 2.5, borderTop: `1px solid ${HAIRLINE}`, flexShrink: 0 }}
          >
            <Button
              onClick={onClose}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                py: 1,
                borderRadius: "10px",
                color: MUTED,
                background: "#fff",
                border: `1px solid ${HAIRLINE}`,
                "&:hover": { background: TILE_BG, borderColor: "#DBE3EF" },
              }}
            >
              Close
            </Button>
            {canEdit && (
              <Button
                onClick={() => onEdit?.(domain)}
                fullWidth
                startIcon={<EditOutlinedIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1,
                  borderRadius: "10px",
                  color: "#fff",
                  background: BLUE,
                  boxShadow: "none",
                  "&:hover": { background: BLUE_DARK, boxShadow: "none" },
                }}
              >
                Edit
              </Button>
            )}
          </Stack>
        </>
      )}
    </Drawer>
  );
}

export default DomainDetailDrawer;
